




































































































import {
  defineComponent,
  PropType,
  ref,
  watch,
  computed,
  ComputedRef,
} from '@vue/composition-api';
import { TabletRenderer } from '@oare/oare';
import {
  EpigraphicUnitSide,
  EditTextAction,
  AddColumnPayload,
  MergeLinePayload,
  MergeWordPayload,
  EpigraphicWord,
  EditorDiscourseWord,
  EpigraphicUnitType,
  MarkupType,
  SearchSpellingResultRow,
} from '@oare/types';
import EditColumn from './EditColumn.vue';
import sl from '@/serviceLocator';
import InsertButton from './InsertButton.vue';
import ConnectDiscourseItem from '@/views/Texts/CollectionTexts/AddTexts/Discourse/components/ConnectDiscourseItem.vue';

export default defineComponent({
  props: {
    renderer: {
      type: Object as PropType<TabletRenderer>,
      required: true,
    },
    side: {
      type: String as PropType<EpigraphicUnitSide>,
      required: true,
    },
    currentEditAction: {
      type: String as PropType<EditTextAction>,
      required: false,
    },
    textUuid: {
      type: String,
      required: true,
    },
  },
  components: {
    EditColumn,
    InsertButton,
    ConnectDiscourseItem,
  },
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const editTextLoading = ref(false);

    const addColumnDialog = ref(false);
    const columnToAdd = ref<number>();
    const addColumn = async () => {
      try {
        editTextLoading.value = true;

        if (columnToAdd.value === undefined) {
          throw new Error('No column selected');
        }

        const payload: AddColumnPayload = {
          type: 'addColumn',
          textUuid: props.textUuid,
          side: props.side,
          column: columnToAdd.value,
        };
        await server.editText(payload);
        resetRenderer();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error adding column. Please try again.',
          err as Error
        );
      } finally {
        addColumnDialog.value = false;
        resetCurrentEditAction();
        editTextLoading.value = false;
        columnToAdd.value = undefined;
      }
    };
    watch(addColumnDialog, () => {
      if (!addColumnDialog.value) {
        columnToAdd.value = undefined;
        resetCurrentEditAction();
      }
    });

    const resetRenderer = () => {
      emit('reset-renderer');
    };

    const resetCurrentEditAction = () => {
      emit('reset-current-edit-action');
    };

    watch(
      () => props.currentEditAction,
      () => {
        if (props.currentEditAction === 'addColumn') {
          addColumnDialog.value = true;
        }
      }
    );

    const mergeLineDialog = ref(false);
    const selectedLines = ref<number[]>([]);
    const handleSelectLine = (line: number) => {
      if (selectedLines.value.includes(line)) {
        selectedLines.value = selectedLines.value.filter(l => l !== line);
      } else {
        selectedLines.value.push(line);
      }
    };
    watch(selectedLines, () => {
      if (selectedLines.value.length === 2) {
        mergeLineDialog.value = true;
      }
    });
    watch(mergeLineDialog, () => {
      if (!mergeLineDialog.value) {
        selectedLines.value = [];
        resetCurrentEditAction();
      }
    });
    const mergeLines = async () => {
      try {
        editTextLoading.value = true;
        if (selectedLines.value.length !== 2) {
          throw new Error('Two lines must be selected in order to merge.');
        }
        const payload: MergeLinePayload = {
          type: 'mergeLine',
          textUuid: props.textUuid,
          firstLine: Math.min(...selectedLines.value),
          secondLine: Math.max(...selectedLines.value),
        };
        await server.editText(payload);
        resetRenderer();
      } catch (err) {
        actions.showErrorSnackbar('Error merging lines. Please try again.');
      } finally {
        selectedLines.value = [];
        mergeLineDialog.value = false;
        resetCurrentEditAction();
        editTextLoading.value = false;
      }
    };

    const mergeWordsDialog = ref(false);
    const selectedWords = ref<EpigraphicWord[]>([]);
    const handleSelectWord = (word: EpigraphicWord) => {
      if (
        selectedWords.value
          .map(w => w.discourseUuid!)
          .includes(word.discourseUuid!)
      ) {
        selectedWords.value = selectedWords.value.filter(
          w => w.discourseUuid! !== word.discourseUuid!
        );
      } else {
        selectedWords.value.push(word);
      }
    };
    watch(selectedWords, () => {
      if (selectedWords.value.length === 2) {
        mergeWordsDialog.value = true;
      }
    });
    watch(mergeWordsDialog, () => {
      if (!mergeWordsDialog.value) {
        selectedWords.value = [];
        mergeWordsFormsLoaded.value = false;
        mergeWordsForm.value = undefined;
        resetCurrentEditAction();
      }
    });
    const mergeWords = async () => {
      try {
        editTextLoading.value = true;
        if (selectedWords.value.length !== 2) {
          throw new Error('Two words must be selected in order to merge.');
        }
        const payload: MergeWordPayload = {
          type: 'mergeWord',
          textUuid: props.textUuid,
          discourseUuids: selectedWords.value.map(w => w.discourseUuid!),
          spelling: getUpdatedSignsWithSeparators(),
          spellingUuid: mergeWordsForm.value
            ? mergeWordsForm.value.spellingUuid
            : null,
          transcription: mergeWordsForm.value
            ? mergeWordsForm.value.form.form
            : null,
        };
        await server.editText(payload);
        resetRenderer();
      } catch (err) {
        actions.showErrorSnackbar('Error merging words. Please try again.');
      } finally {
        selectedWords.value = [];
        mergeWordsDialog.value = false;
        resetCurrentEditAction();
        editTextLoading.value = false;
      }
    };
    const mergeWordsFormsLoaded = ref(false);
    const mergeWordsForm = ref<SearchSpellingResultRow>();
    const editorDiscourseWord: ComputedRef<EditorDiscourseWord | undefined> =
      computed(() => {
        if (selectedWords.value.length === 0) {
          return undefined;
        }
        const newWord = getUpdatedSignsWithSeparators();
        return {
          discourseUuid: selectedWords.value[0].discourseUuid,
          spelling: newWord,
          type: 'word',
        };
      });

    const getUpdatedSignsWithSeparators = () => {
      if (selectedWords.value.length !== 2) {
        return '';
      }

      const firstMin = Math.min(
        ...selectedWords.value[0].signs.map(sign => sign.objOnTablet)
      );
      const secondMin = Math.min(
        ...selectedWords.value[1].signs.map(sign => sign.objOnTablet)
      );

      const firstIndexIsFirstPiece = firstMin < secondMin;

      const firstPieces: {
        reading: string;
        type: EpigraphicUnitType | null;
        markup: MarkupType[];
      }[] = selectedWords.value[firstIndexIsFirstPiece ? 0 : 1].signs.map(
        sign => ({
          reading: sign.reading || '',
          type: sign.type,
          markup: sign.markups.map(unit => unit.type),
        })
      );

      const secondPieces: {
        reading: string;
        type: EpigraphicUnitType | null;
        markup: MarkupType[];
      }[] = selectedWords.value[firstIndexIsFirstPiece ? 1 : 0].signs.map(
        sign => ({
          reading: sign.reading || '',
          type: sign.type,
          markup: sign.markups.map(unit => unit.type),
        })
      );

      const newPieces = [...firstPieces, ...secondPieces];

      const newWord = newPieces.map((sign, index) => {
        const nextSign =
          index !== newPieces.length - 1 ? newPieces[index + 1] : null;

        let newSeparator = '';
        if (nextSign) {
          if (
            !sign.markup.includes('phoneticComplement') &&
            nextSign.markup.includes('phoneticComplement')
          ) {
            newSeparator = '';
          } else if (
            sign.type === 'determinative' ||
            nextSign.type === 'determinative'
          ) {
            newSeparator = '';
          } else if (
            sign.type === 'phonogram' ||
            nextSign.type === 'phonogram'
          ) {
            newSeparator = '-';
          } else if (sign.type === 'number' && nextSign.type === 'number') {
            newSeparator = '+';
          } else if (sign.type === 'logogram' || nextSign.type === 'logogram') {
            newSeparator = '.';
          }
        }

        let newReading = sign.reading;
        if (sign.type === 'determinative') {
          newReading = `(${newReading})`;
        }

        return {
          ...sign,
          reading: newReading,
          separator: newSeparator,
        };
      });

      let newWordReading = '';
      newWord.forEach(sign => {
        newWordReading += sign.reading;
        if (sign.separator) {
          newWordReading += sign.separator;
        }
      });
      return newWordReading
        .replace(/([[\]{}⸢⸣«»‹›:;*?\\!])|(".+")|('.+')|(^\/)+/g, '')
        .replace(/<[^>]*>/g, '');
    };

    return {
      editTextLoading,
      addColumnDialog,
      addColumn,
      resetRenderer,
      resetCurrentEditAction,
      selectedLines,
      handleSelectLine,
      mergeLineDialog,
      mergeLines,
      columnToAdd,
      mergeWordsDialog,
      selectedWords,
      handleSelectWord,
      mergeWords,
      mergeWordsFormsLoaded,
      mergeWordsForm,
      editorDiscourseWord,
      getUpdatedSignsWithSeparators,
    };
  },
});
